import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  PixelRatio,
  Platform,
  useWindowDimensions,
  Alert,
  Modal,
  TextInput,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { Ionicons } from '@expo/vector-icons';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import SocialSaveBookmarkButton from './Social/SocialSaveBookmarkButton';
// ShareBiteModal will be rendered at the screen level to avoid bottom sheet nesting issues

const { width: screenWidth } = Dimensions.get('window');
const cardPadding = 16;
const mediaWidth = screenWidth - 32; // Account for card margins

/** One-photo and dual / carousel full-bleed media — same height everywhere (feed, Shared Bites, profiles). */
const DUAL_FULL_BLEED_HEIGHT = 392;

/** Use for list `getItemLayout` math so it tracks `DUAL_FULL_BLEED_HEIGHT` changes. */
export const MOMENT_CARD_MEDIA_HEIGHT = DUAL_FULL_BLEED_HEIGHT;

const MomentCard = ({
  moment,
  onCommentPress,
  privyDid,
  onMomentUpdated,
  onRestaurantPress,
  onSaveListPress,
  showCommentsInline = false,
  onCommentInputFocus,
  isLiked: initialIsLiked = false,
  onSharePress,
  onUsernamePress,
  onAvatarPress,
  onEditPress,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  /** cardContainer + mediaContainer horizontal margins (16×4) — used when layout hasn’t measured yet (e.g. Social restaurant sheet) */
  const estimatedCarouselWidth = useMemo(
    () => Math.max(1, PixelRatio.roundToNearestPixel(windowWidth - 64)),
    [windowWidth]
  );

  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likesCount, setLikesCount] = useState(moment.likesCount || 0);
  const [isFrontPhotoActive, setIsFrontPhotoActive] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  /** 0 = dual overlay for photoUrls[0] & [1]; 1+ = full-bleed photo at index carouselPage + 1 */
  const [carouselPage, setCarouselPage] = useState(0);
  const mediaScrollRef = useRef(null);
  const carouselViewportWidthRef = useRef(0);
  /** Avoid setState on every onLayout (float jitter / parent scroll) — that caused infinite re-renders + native stack overflow */
  const lastCarouselLayoutWidthRef = useRef(0);
  /** Measured width only (3+ photos). Start at 0 so paging uses real width inside bottom sheets — not screenWidth-32. */
  const [carouselViewportWidth, setCarouselViewportWidth] = useState(0);

  const photoUrls = useMemo(() => {
    const raw = (moment.photoUrls || []).filter((u) => u != null && u !== '');
    if (raw.length > 0) return raw;
    return [moment.frontPhotoUrl, moment.backPhotoUrl].filter(Boolean);
  }, [moment.photoUrls, moment.frontPhotoUrl, moment.backPhotoUrl]);

  /** When Convex fills in more URLs for the same moment, re-measure the carousel */
  const photoUrlsSignature = useMemo(() => photoUrls.join('\0'), [photoUrls]);

  useEffect(() => {
    setCarouselPage(0);
    setIsFrontPhotoActive(false);
    carouselViewportWidthRef.current = 0;
    lastCarouselLayoutWidthRef.current = 0;
    setCarouselViewportWidth(0);
    requestAnimationFrame(() => {
      mediaScrollRef.current?.scrollTo({ x: 0, y: 0, animated: false });
    });
  }, [moment._id, photoUrlsSignature]);

  // After layout gives the true width (esp. nested in Map bottom sheet), align scroll offset — not on every page change (that would fight user swipes).
  useEffect(() => {
    if (photoUrls.length <= 2) return;
    const w = carouselViewportWidth > 0 ? carouselViewportWidth : estimatedCarouselWidth;
    const x = carouselPage * w;
    requestAnimationFrame(() => {
      mediaScrollRef.current?.scrollTo({ x, y: 0, animated: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only when width or moment changes, not when user swipes
  }, [carouselViewportWidth, estimatedCarouselWidth, photoUrls.length, moment._id]);

  // Preload all photos for carousel / dual toggle
  useEffect(() => {
    const preloadImages = async () => {
      if (photoUrls.length === 0) {
        setImagesPreloaded(true);
        return;
      }
      try {
        await Promise.all(photoUrls.map((u) => Image.prefetch(u)));
        setImagesPreloaded(true);
      } catch (error) {
        setImagesPreloaded(true);
      }
    };

    preloadImages();
  }, [photoUrls]);

  // Toggle photo function - instant swap
  const togglePhoto = () => {
    if (carouselPage !== 0 || photoUrls.length < 2) return;
    if (isToggling || !imagesPreloaded) return;

    setIsToggling(true);
    setIsFrontPhotoActive(!isFrontPhotoActive);
    setTimeout(() => {
      setIsToggling(false);
    }, 100);
  };

  const getCurrentSharePhotoIndex = useCallback(() => {
    if (photoUrls.length === 0) return 0;
    if (photoUrls.length === 1) return 0;
    if (photoUrls.length === 2) return isFrontPhotoActive ? 0 : 1;
    if (carouselPage === 0) return isFrontPhotoActive ? 0 : 1;
    return carouselPage + 1;
  }, [photoUrls.length, carouselPage, isFrontPhotoActive]);

  // Update isLiked when initialIsLiked prop changes
  useEffect(() => {
    setIsLiked(initialIsLiked);
  }, [initialIsLiked]);

  // Update likesCount when moment prop changes (e.g., after refresh)
  useEffect(() => {
    setLikesCount(moment.likesCount || 0);
  }, [moment.likesCount]);
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Share modal state removed - handled at screen level
  const [editRating, setEditRating] = useState(moment.rating || 0);
  const [editCaption, setEditCaption] = useState(moment.caption || '');
  const [editVisibility, setEditVisibility] = useState(moment.visibility || 'friends');
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [captionTruncated, setCaptionTruncated] = useState(false);
  const [captionMeasured, setCaptionMeasured] = useState(false);

  useEffect(() => {
    setCaptionExpanded(false);
    setCaptionTruncated(false);
    setCaptionMeasured(false);
  }, [moment._id, moment.caption]);

  // Relative time function - based on calendar date changes
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    
    // Get calendar dates (year, month, day)
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const timeDate = new Date(time.getFullYear(), time.getMonth(), time.getDate());
    
    // Calculate the difference in calendar days
    const diffInMs = nowDate.getTime() - timeDate.getTime();
    const daysDiff = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(daysDiff / 365);
    const months = Math.floor(daysDiff / 30);
    const weeks = Math.floor(daysDiff / 7);

    if (years > 0) return `${years}y`;
    if (months > 0) return `${months}m`;
    if (weeks > 0) return `${weeks}w`;
    if (daysDiff > 0) return `${daysDiff}d`;
    if (daysDiff === 0) return 'today';
    return 'now';
  };

  const toggleLike = useMutation(api.moments.toggleLike);
  const updateMoment = useMutation(api.moments.updateMoment);
  const deleteMoment = useMutation(api.moments.deleteMoment);
  const createComment = useMutation(api.moments.createComment);
  const deleteComment = useMutation(api.moments.deleteComment);
  
  // Get comments if showing inline
  const commentsData = useQuery(
    api.moments.getCommentsByMoment,
    showCommentsInline ? { momentId: moment._id } : "skip"
  );

  const handleLikePress = async () => {
    if (!privyDid) return;
    
    try {
      const result = await toggleLike({ 
        momentId: moment._id,
        privyDid: privyDid
      });
      setIsLiked(result.liked);
      setLikesCount(prev => result.liked ? prev + 1 : Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleCommentPress = () => {
    if (onCommentPress) {
      onCommentPress(moment);
    }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !privyDid || isSubmittingComment) return;
    
    setIsSubmittingComment(true);
    try {
      await createComment({
        momentId: moment._id,
        privyDid: privyDid,
        text: commentText.trim()
      });
      setCommentText('');
      // Comments will refresh automatically due to the query
    } catch (error) {
      console.error('Error creating comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!privyDid) return;
    
    Alert.alert(
      'Delete Comment',
      'Are you sure you want to delete this comment?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteComment({
                commentId: commentId,
                privyDid: privyDid
              });
              // Comments will refresh automatically due to the query
            } catch (error) {
              console.error('Error deleting comment:', error);
              Alert.alert('Error', 'Failed to delete comment. Please try again.');
            }
          }
        }
      ]
    );
  };

  const handleEditPress = () => {
    setShowMenu(false);
    // If onEditPress is provided, use it (for rendering modal at screen level to avoid bottom sheet nesting issues)
    if (onEditPress) {
      onEditPress(moment);
    } else {
      // Reset edit state to current moment values
      setEditRating(moment.rating || 0);
      setEditCaption(moment.caption || '');
      setEditVisibility(moment.visibility || 'friends');
      setShowEditModal(true);
    }
  };

  const handleSharePress = () => {
    setShowMenu(false);
    if (onSharePress) {
      onSharePress(moment, getCurrentSharePhotoIndex());
    }
  };

  const handleDeletePress = () => {
    setShowMenu(false);
    Alert.alert(
      'Delete Moment',
      'Are you sure you want to delete this moment? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteMoment({ momentId: moment._id, privyDid });
              
              // Trigger refresh in parent component
              if (onMomentUpdated) {
                onMomentUpdated();
              }
              
              // Show success feedback
              Alert.alert('Success', 'Moment deleted successfully!');
            } catch (error) {
              console.error('Error deleting moment:', error);
              Alert.alert('Error', 'Failed to delete moment. Please try again.');
            }
          }
        }
      ]
    );
  };

  const handleSaveEdit = async () => {
    try {
      await updateMoment({
        momentId: moment._id,
        privyDid,
        rating: editRating,
        caption: editCaption,
        visibility: editVisibility
      });
      setShowEditModal(false);
      setShowMenu(false);
      
      // Trigger refresh in parent component
      if (onMomentUpdated) {
        onMomentUpdated();
      }
      
      // Show success feedback
      Alert.alert('Success', 'Moment updated successfully!');
    } catch (error) {
      console.error('Error updating moment:', error);
      Alert.alert('Error', 'Failed to update moment. Please try again.');
    }
  };

  const isOwnMoment = moment.user?.privyDid === privyDid;

  /** Dots match swipe pages: for 3+ photos, first two images share one dot (dual overlay). */
  const renderPhotoDots = (dotCount, activeDotIndex) => (
    <View style={styles.carouselDots} pointerEvents="none">
      {Array.from({ length: dotCount }).map((_, i) => (
        <View
          key={i}
          style={[
            styles.carouselDot,
            i === activeDotIndex && styles.carouselDotActive,
          ]}
        />
      ))}
    </View>
  );

  const renderDualSlide = () => {
    const activePhotoUrl = isFrontPhotoActive ? photoUrls[0] : photoUrls[1];
    const overlayPhotoUrl = isFrontPhotoActive ? photoUrls[1] : photoUrls[0];
    return (
      <View style={styles.dualPhotoContainer}>
        <Image
          source={{ uri: activePhotoUrl }}
          style={styles.dualPhotoBackground}
          resizeMode="cover"
        />
        <TouchableOpacity
          style={styles.dualPhotoOverlay}
          onPress={togglePhoto}
          activeOpacity={0.8}
        >
          <Image
            source={{ uri: overlayPhotoUrl }}
            style={styles.dualPhotoForeground}
            resizeMode="cover"
          />
        </TouchableOpacity>
      </View>
    );
  };

  const handleMediaScrollEnd = (e) => {
    if (photoUrls.length <= 2) return;
    const w = carouselViewportWidthRef.current;
    if (w <= 0) return;
    const numSlides = photoUrls.length - 1;
    const raw = e.nativeEvent.contentOffset.x;
    const page = Math.round(raw / w);
    const clamped = Math.max(0, Math.min(page, numSlides - 1));
    const targetX = clamped * w;
    // Paging drifts when child width ≠ scroll view width; nudge to exact page offset
    if (Math.abs(raw - targetX) > 2) {
      mediaScrollRef.current?.scrollTo({ x: targetX, y: 0, animated: true });
    }
    setCarouselPage(clamped);
  };

  const renderMedia = () => {
    if (photoUrls.length === 0) {
      return null;
    }

    if (photoUrls.length === 1) {
      return (
        <Image
          source={{ uri: photoUrls[0] }}
          style={styles.singlePhoto}
          resizeMode="cover"
        />
      );
    }

    // Exactly two photos: dual overlay only; no dots
    if (photoUrls.length === 2) {
      return (
        <View style={styles.mediaCarouselWrap}>
          {renderDualSlide()}
        </View>
      );
    }

    // 3+ photos: dots = one per page (dual slide = 1 dot, then one dot per remaining photo)
    const pageDotCount = photoUrls.length - 1;

    const applyCarouselWidth = (layoutWidth) => {
      const w = layoutWidth > 0 ? PixelRatio.roundToNearestPixel(layoutWidth) : 0;
      if (w <= 0) return;
      if (w === lastCarouselLayoutWidthRef.current) return;
      lastCarouselLayoutWidthRef.current = w;
      carouselViewportWidthRef.current = w;
      setCarouselViewportWidth(w);
    };

    const onCarouselLayout = (e) => {
      applyCarouselWidth(e.nativeEvent.layout.width);
    };

    /** Refines width to match the scroll view’s frame (what snap/paging uses) */
    const onScrollViewLayout = (e) => {
      applyCarouselWidth(e.nativeEvent.layout.width);
    };

    const effectiveCarouselWidth =
      carouselViewportWidth > 0 ? carouselViewportWidth : estimatedCarouselWidth;
    carouselViewportWidthRef.current = effectiveCarouselWidth;

    return (
      <View collapsable={false} style={styles.mediaCarouselWrap} onLayout={onCarouselLayout}>
        <ScrollView
          ref={mediaScrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled
          directionalLockEnabled
          decelerationRate="fast"
          snapToInterval={effectiveCarouselWidth}
          snapToAlignment="start"
          disableIntervalMomentum={Platform.OS === 'ios'}
          style={styles.mediaCarouselScroll}
          onLayout={onScrollViewLayout}
          onMomentumScrollEnd={handleMediaScrollEnd}
        >
          <View style={{ width: effectiveCarouselWidth }}>{renderDualSlide()}</View>
          {photoUrls.slice(2).map((url, i) => (
            <View key={`extra-${i}`} style={{ width: effectiveCarouselWidth }}>
              <Image source={{ uri: url }} style={styles.carouselFullPhoto} resizeMode="cover" />
            </View>
          ))}
        </ScrollView>
        {renderPhotoDots(pageDotCount, carouselPage)}
      </View>
    );
  };

  const renderCravePill = () => {
    const r = moment.rating;
    if (r == null || r < 1 || r > 5) return null;
    let inner;
    if (r === 5) {
      inner = (
        <>
          <Text style={styles.craveEmoji}>🤤</Text>
          <Text style={styles.craveText}>crave</Text>
        </>
      );
    } else if (r >= 3) {
      inner = (
        <>
          <Text style={styles.craveEmoji}>😌</Text>
          <Text style={styles.craveText}>solid</Text>
        </>
      );
    } else {
      inner = (
        <>
          <Text style={styles.craveEmoji}>🫢</Text>
          <Text style={styles.craveText}>toss</Text>
        </>
      );
    }
    return <View style={styles.cravePill}>{inner}</View>;
  };

  const restaurantNameDisplay = useMemo(() => {
    const name = moment.restaurant?.name || 'Unknown Restaurant';
    if (name.length > 18) {
      return `${name.slice(0, 18)}...`;
    }
    return name;
  }, [moment.restaurant?.name]);

  const mediaContent = renderMedia();

  return (
    <View style={[styles.cardContainer, showMenu && styles.cardContainerMenuOpen]}>
      <View style={styles.card}>
      <TouchableOpacity
        style={styles.headerTouchableWrap}
        activeOpacity={1}
        onPress={() => setShowMenu(false)}
      >
      {/* Header with Profile Picture and User Info */}
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.avatarContainer}
          onPress={() => onAvatarPress && onAvatarPress(moment.user)}
          activeOpacity={0.7}
        >
          {moment.user?.avatarUrl ? (
            <Image
              source={{ uri: moment.user.avatarUrl }}
              style={styles.avatar}
            />
          ) : (
            <Image
              source={require('../assets/DisgoBrownLogo.png')}
              style={styles.avatar}
              resizeMode="cover"
            />
          )}
        </TouchableOpacity>
        <View style={styles.userInfo}>
          <View style={styles.usernameRow}>
            <TouchableOpacity onPress={() => onUsernamePress && onUsernamePress(moment.user)}>
              <Text style={styles.username}>@{moment.user?.username || 'unknown'}</Text>
            </TouchableOpacity>
            {/* Tagged Friends */}
            {moment.taggedFriends && moment.taggedFriends.length > 0 && (
              <View style={styles.taggedFriendsContainer}>
                <Text style={styles.taggedFriendsPrefix}> with </Text>
                {moment.taggedFriends.map((friend, index) => (
                  <React.Fragment key={friend._id || index}>
                    <TouchableOpacity
                      onPress={() => onUsernamePress && onUsernamePress(friend)}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.taggedFriendUsername}>
                        @{friend.username || friend.name || 'friend'}
                      </Text>
                    </TouchableOpacity>
                    {index < moment.taggedFriends.length - 1 && (
                      <Text style={styles.taggedFriendsPrefix}> </Text>
                    )}
                  </React.Fragment>
                ))}
              </View>
            )}
          </View>
          <View style={styles.checkinRow}>
            <Text
              style={styles.restaurantName}
              onPress={() => onRestaurantPress && onRestaurantPress(moment.restaurant)}
            >
              {restaurantNameDisplay}
            </Text>
            {renderCravePill()}
          </View>
        </View>
        {isOwnMoment && (
          <TouchableOpacity 
            style={styles.menuButton}
            onPress={() => setShowMenu(!showMenu)}
          >
            <Ionicons name="ellipsis-horizontal" size={18} color="#8B8B8B" />
          </TouchableOpacity>
        )}
      </View>

      </TouchableOpacity>

      {/* Media: outside dismiss tap target so horizontal swipe / gesture scroll works */}
      {mediaContent ? (
        <View style={styles.mediaContainer}>{mediaContent}</View>
      ) : null}

      <TouchableOpacity
        activeOpacity={1}
        onPress={() => setShowMenu(false)}
      >
      {/* Caption above like / comment row */}
      {moment.caption ? (
        <View style={styles.captionAboveActions}>
          <Text
            pointerEvents="none"
            style={[styles.captionText, styles.captionMeasureHidden]}
            onTextLayout={(e) => {
              setCaptionTruncated(e.nativeEvent.lines.length > 1);
              setCaptionMeasured(true);
            }}
          >
            {moment.caption}
          </Text>
          {!captionMeasured ? (
            <Text style={styles.captionText} numberOfLines={1}>
              {moment.caption}
            </Text>
          ) : captionTruncated && !captionExpanded ? (
            <View style={styles.captionRow}>
              <Text style={[styles.captionText, styles.captionTextFlex]} numberOfLines={1}>
                {moment.caption}
              </Text>
              <TouchableOpacity
                onPress={() => setCaptionExpanded(true)}
                hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}
                activeOpacity={0.7}
              >
                <Text style={styles.captionMore}>more</Text>
              </TouchableOpacity>
            </View>
          ) : captionTruncated && captionExpanded ? (
            <Text style={styles.captionText}>
              {moment.caption}
              <Text
                style={styles.captionMore}
                onPress={() => setCaptionExpanded(false)}
                suppressHighlighting={false}
              >
                {' '}
                less
              </Text>
            </Text>
          ) : (
            <Text style={styles.captionText}>{moment.caption}</Text>
          )}
        </View>
      ) : null}

      <View style={styles.bottomActionsRow}>
        <View style={[styles.bottomActions, mediaContent && styles.bottomActionsPhoto]}>
          <TouchableOpacity 
            style={styles.bottomActionPill} 
            onPress={handleLikePress}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Text style={[styles.bottomActionIcon, isLiked && styles.likedIcon]}>
              {isLiked ? '♥' : '♡'}
            </Text>
            <Text style={styles.bottomActionCount}>{likesCount}</Text>
          </TouchableOpacity>
          
          {!showCommentsInline && (
            <TouchableOpacity 
              style={styles.bottomActionPill} 
              onPress={handleCommentPress}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="chatbubble-outline" size={20} color="#522504" style={{ marginRight: 6 }} />
              <Text style={styles.bottomActionCount}>{moment.commentsCount || 0}</Text>
            </TouchableOpacity>
          )}
          {moment.restaurant?._id && privyDid && onSaveListPress ? (
            <View style={styles.bottomActionPill}>
              <SocialSaveBookmarkButton
                restaurantId={moment.restaurant._id}
                privyDid={privyDid}
                onPress={onSaveListPress}
                size={20}
                iconColor="#522504"
              />
            </View>
          ) : null}
        </View>
        {(moment.isViewerFriend !== false) && (
          <Text style={styles.timeText}>{getRelativeTime(moment.createdAt)}</Text>
        )}
      </View>

      </TouchableOpacity>

      {/* Menu: render after media so sibling order paints above photos (RN z-index alone is not enough) */}
      {showMenu && isOwnMoment && (
        <View style={styles.menuDropdown}>
          <TouchableOpacity style={styles.menuItem} onPress={handleSharePress}>
            <Ionicons name="share-outline" size={16} color="#522504" />
            <Text style={styles.menuItemText}>Share Bite</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.menuItem} onPress={handleEditPress}>
            <Ionicons name="create-outline" size={16} color="#522504" />
            <Text style={styles.menuItemText}>Edit</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.menuItem} onPress={handleDeletePress}>
            <Ionicons name="trash-outline" size={16} color="#FF3B30" />
            <Text style={[styles.menuItemText, { color: '#FF3B30' }]}>Delete</Text>
          </TouchableOpacity>
        </View>
      )}
      </View>

      {/* Edit Modal */}
      <Modal
        visible={showEditModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowEditModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Edit Moment</Text>
            
            <View style={styles.ratingSection}>
              <Text style={styles.modalLabel}>Rating</Text>
              <View style={styles.starContainer}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <TouchableOpacity
                    key={star}
                    onPress={() => setEditRating(star)}
                    style={styles.starButton}
                  >
                    <Text style={[
                      styles.star,
                      star <= editRating ? styles.starFilled : styles.starEmpty
                    ]}>
                      ★
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.captionSection}>
              <Text style={styles.modalLabel}>Caption</Text>
              <TextInput
                style={[styles.captionInput, { color: '#000000' }]}
                value={editCaption}
                onChangeText={setEditCaption}
                placeholder="Add a caption..."
                multiline
                maxLength={200}
              />
            </View>

            <View style={styles.visibilitySection}>
              <Text style={styles.modalLabel}>Who can see this?</Text>
              <View style={styles.visibilityOptions}>
                <TouchableOpacity
                  style={[
                    styles.visibilityOption,
                    editVisibility === 'friends' && styles.visibilityOptionSelected
                  ]}
                  onPress={() => setEditVisibility('friends')}
                >
                  <Ionicons
                    name={editVisibility === 'friends' ? 'radio-button-on' : 'radio-button-off'}
                    size={20}
                    color={editVisibility === 'friends' ? '#8B4513' : '#A0A0A0'}
                  />
                  <Text style={[
                    styles.visibilityOptionText,
                    editVisibility === 'friends' && styles.visibilityOptionTextSelected
                  ]}>
                    Friends Only
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.visibilityOption,
                    editVisibility === 'public' && styles.visibilityOptionSelected
                  ]}
                  onPress={() => setEditVisibility('public')}
                >
                  <Ionicons
                    name={editVisibility === 'public' ? 'radio-button-on' : 'radio-button-off'}
                    size={20}
                    color={editVisibility === 'public' ? '#8B4513' : '#A0A0A0'}
                  />
                  <Text style={[
                    styles.visibilityOptionText,
                    editVisibility === 'public' && styles.visibilityOptionTextSelected
                  ]}>
                    Public
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.visibilityOption,
                    editVisibility === 'only_me' && styles.visibilityOptionSelected
                  ]}
                  onPress={() => setEditVisibility('only_me')}
                >
                  <Ionicons
                    name={editVisibility === 'only_me' ? 'radio-button-on' : 'radio-button-off'}
                    size={20}
                    color={editVisibility === 'only_me' ? '#8B4513' : '#A0A0A0'}
                  />
                  <Text style={[
                    styles.visibilityOptionText,
                    editVisibility === 'only_me' && styles.visibilityOptionTextSelected
                  ]}>
                    Only me
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowEditModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.saveButton}
                onPress={handleSaveEdit}
              >
                <Text style={styles.saveButtonText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Inline Comments Section */}
      {showCommentsInline && (
        <View style={styles.commentsSection}>
          <Text style={styles.commentsTitle}>Comments</Text>
           {commentsData && commentsData.length > 0 ? (
             commentsData.map((comment, index) => {
               const isOwnComment = comment.user?.privyDid === privyDid;
               return (
                 <View key={`${comment._id}-${index}`} style={styles.commentItem}>
                   <View style={styles.commentHeader}>
                     <Text style={styles.commentUsername}>{comment.user?.username || 'Unknown'}</Text>
                     {/* Only show comment time if viewer is a friend */}
                     {(moment.isViewerFriend !== false) && (
                       <Text style={styles.commentTime}>{getRelativeTime(comment.createdAt)}</Text>
                     )}
                     {isOwnComment && (
                       <TouchableOpacity
                         style={styles.deleteCommentButton}
                         onPress={() => handleDeleteComment(comment._id)}
                         hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                       >
                         <Ionicons name="trash-outline" size={14} color="#FF3B30" />
                       </TouchableOpacity>
                     )}
                   </View>
                   <Text style={styles.commentText}>{comment.text}</Text>
                 </View>
               );
             })
           ) : (
            <Text style={styles.noCommentsText}>Be the first to comment!</Text>
          )}
          
          {/* Add Comment Input */}
          <View style={styles.addCommentSection}>
             <TextInput
               style={[styles.commentInput, { color: '#000000' }]}
               placeholder="Add a comment..."
               value={commentText}
               onChangeText={setCommentText}
               onFocus={onCommentInputFocus}
               multiline
               maxLength={200}
             />
            <TouchableOpacity
              style={[styles.submitCommentButton, (!commentText.trim() || isSubmittingComment) && styles.submitCommentButtonDisabled]}
              onPress={handleSubmitComment}
              disabled={!commentText.trim() || isSubmittingComment}
            >
              <Text style={styles.submitCommentText}>
                {isSubmittingComment ? 'Posting...' : 'Post'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Share Bite Modal removed - handled at screen level */}
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    position: 'relative',
    marginHorizontal: 16,
    marginVertical: 6,
  },
  cardContainerMenuOpen: {
    zIndex: 2000,
    elevation: 30,
  },
  headerTouchableWrap: {
    position: 'relative',
    zIndex: 10,
    elevation: 4,
  },
  card: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F0F0F0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 8,
    paddingTop: 4,
    paddingBottom: 4,
    position: 'relative',
  },
  avatarContainer: {
    width: 56,
    height: 56,
    borderRadius: 30,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: '#F0F0F0',
    marginRight: 20,
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  userInfo: {
    flex: 1,
  },
  usernameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 0,
    marginTop: 8,
  },
  username: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1A1A1A',
    letterSpacing: -0.2,
  },
  timeText: {
    fontSize: 12,
    color: '#8B8B8B',
    fontWeight: '500',
  },
  checkinText: {
    fontSize: 15,
    color: '#8B8B8B',
    fontWeight: '500',
  },
  checkinRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  restaurantName: {
    textDecorationLine: 'underline',
    color: '#9C6B42',
    fontWeight: '600',
  },
  headerActions: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  headerActionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF', // Solid background for efficient shadow calculation
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 0,
    opacity: 0.9, // Use opacity instead of rgba for better shadow performance
  },
  headerActionIcon: {
    fontSize: 24,
    marginRight: 4,
    color: '#522504',
    fontWeight: 'normal',
  },
  headerActionCount: {
    fontSize: 11,
    fontWeight: '700',
    color: '#522504',
  },
  captionAboveActions: {
    position: 'relative',
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 8,
    overflow: 'hidden',
  },
  captionMeasureHidden: {
    position: 'absolute',
    opacity: 0,
    left: 0,
    right: 0,
    top: 0,
    zIndex: -1,
  },
  captionRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 6,
  },
  captionTextFlex: {
    flex: 1,
    minWidth: 0,
  },
  captionMore: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '700',
    color: '#9C6B42',
  },
  captionContainer: {
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  cravePill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E8E8E8',
  },
  craveEmoji: {
    fontSize: 12,
    marginRight: 4,
  },
  craveText: {
    fontSize: 11,
    color: '#8B8B8B',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  captionText: {
    fontSize: 14,
    color: '#522504',
    lineHeight: 20,
    fontWeight: '500',
  },
  mediaContainer: {
    position: 'relative',
    zIndex: 0,
    marginHorizontal: 16,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 0,
  },
  mediaCarouselWrap: {
    position: 'relative',
    width: '100%',
  },
  mediaCarouselScroll: {
    width: '100%',
  },
  carouselFullPhoto: {
    width: '100%',
    height: DUAL_FULL_BLEED_HEIGHT,
  },
  carouselDots: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  carouselDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginHorizontal: 3,
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
  carouselDotActive: {
    backgroundColor: '#FFFFFF',
    width: 8,
    height: 8,
    borderRadius: 4,
    marginHorizontal: 2,
  },
  singlePhoto: {
    width: '100%',
    height: DUAL_FULL_BLEED_HEIGHT,
  },
  dualPhotoContainer: {
    position: 'relative',
    width: '100%',
    height: DUAL_FULL_BLEED_HEIGHT,
  },
  dualPhotoBackground: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  photoLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  dualPhotoImage: {
    width: '100%',
    height: '100%',
  },
  dualPhotoOverlay: {
    position: 'absolute',
    top: 16,
    left: 16,
    width: 100,
    height: 140,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: '#D4C4B8',
    backgroundColor: '#FFFFFF', // Solid background for efficient shadow calculation
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 4,
  },
  dualPhotoForeground: {
    width: '100%',
    height: '100%',
  },
  placeholderContainer: {
    width: '100%',
    height: DUAL_FULL_BLEED_HEIGHT,
    backgroundColor: '#F8F8F8',
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    fontSize: 24,
    color: '#C4C4C4',
  },
  overlayActions: {
    position: 'absolute',
    bottom: 16,
    right: 0,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
  },
  actionPill: {
    backgroundColor: '#FFFFFF', // Solid background for efficient shadow calculation
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minWidth: 60,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 0,
    opacity: 0.9, // Use opacity instead of rgba for better shadow performance
  },
  actionIcon: {
    fontSize: 20,
    marginBottom: 2,
    color: '#522504',
    fontWeight: 'normal',
  },
  likedIcon: {
    color: '#522504',
  },
  actionCount: {
    fontSize: 12,
    fontWeight: '700',
    color: '#522504',
  },
  bottomActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 0,
    paddingBottom: 4,
    gap: 8,
  },
  bottomActions: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minWidth: 0,
    gap: 8,
  },
  bottomActionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  bottomActionIcon: {
    fontSize: 20,
    marginRight: 4,
    color: '#522504',
    fontWeight: 'normal',
  },
  bottomActionCount: {
    fontSize: 12,
    fontWeight: '700',
    color: '#522504',
  },
  bottomActionsPhoto: {
    gap: 8,
  },
  menuButton: {
    padding: 4,
    borderRadius: 16,
    position: 'absolute',
    top: 8,
    right: 10,
  },
  menuDropdown: {
    position: 'absolute',
    top: 40,
    right: 8,
    backgroundColor: 'white',
    borderRadius: 12,
    paddingVertical: 8,
    paddingHorizontal: 4,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 24,
    zIndex: 1000,
    minWidth: 120,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  menuItemText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1A1A1A',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1A1A1A',
    textAlign: 'center',
    marginBottom: 24,
  },
  ratingSection: {
    marginBottom: 24,
  },
  modalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1A1A1A',
    marginBottom: 12,
  },
  starContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  starButton: {
    padding: 4,
  },
  star: {
    fontSize: 32,
  },
  starFilled: {
    color: '#FFD700',
  },
  starEmpty: {
    color: '#E0E0E0',
  },
  captionSection: {
    marginBottom: 24,
  },
  visibilitySection: {
    marginBottom: 24,
  },
  visibilityOptions: {
    flexDirection: 'row',
    gap: 12,
  },
  visibilityOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    backgroundColor: '#FFFFFF',
    flex: 1,
  },
  visibilityOptionSelected: {
    borderColor: '#8B4513',
    backgroundColor: '#F8F6F4',
  },
  visibilityOptionText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#8B8B8B',
    marginLeft: 8,
  },
  visibilityOptionTextSelected: {
    color: '#8B4513',
    fontWeight: '600',
  },
  captionInput: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#8B8B8B',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#522504',
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  commentsSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingBottom: 100, // Increased to avoid navigation bar
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },
  commentsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1A1A1A',
    marginBottom: 12,
  },
  commentItem: {
    marginBottom: 12,
  },
  commentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  commentUsername: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1A1A1A',
    marginRight: 8,
  },
  commentTime: {
    fontSize: 12,
    color: '#8B8B8B',
    fontWeight: '500',
    flex: 1,
  },
  deleteCommentButton: {
    padding: 4,
    marginLeft: 8,
  },
  commentText: {
    fontSize: 14,
    color: '#1A1A1A',
    lineHeight: 20,
  },
  noCommentsText: {
    fontSize: 14,
    color: '#8B8B8B',
    fontStyle: 'italic',
    textAlign: 'center',
    marginVertical: 12,
  },
  addCommentSection: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 12,
    gap: 8,
  },
  commentInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: '#F9F9F9',
    maxHeight: 80,
    color: '#000000',
  },
  submitCommentButton: {
    backgroundColor: '#522504',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minWidth: 60,
    alignItems: 'center',
  },
  submitCommentButtonDisabled: {
    backgroundColor: '#CCCCCC',
  },
  submitCommentText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  taggedFriendsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  taggedFriendsPrefix: {
    fontSize: 15,
    color: '#8B8B8B',
    fontWeight: '500',
  },
  taggedFriendUsername: {
    fontSize: 15,
    color: '#9C6B42',
    fontWeight: '600',
  },
});

export default MomentCard;
